import { VgApiResult, vgApiRequest, vgPublicRequest } from "./vg-api.service";

/**
 * A cemetery's grave plots. Mirrors api/Funely/GravePlot.cs: plots are Funely rows,
 * and a reservation or sale is a Core service order the plot records.
 */
export const PLOT_TYPE = { SINGLE: 1, DOUBLE: 2, URN: 3, FAMILY: 4 } as const;
export const PLOT_STATUS = { AVAILABLE: 1, RESERVED: 2, SOLD: 3 } as const;

export type PlotType = (typeof PLOT_TYPE)[keyof typeof PLOT_TYPE];
export type PlotStatus = (typeof PLOT_STATUS)[keyof typeof PLOT_STATUS];

export const PLOT_TYPE_LABEL: Record<PlotType, string> = {
  1: "Single plot",
  2: "Double plot",
  3: "Urn plot",
  4: "Family plot",
};

export const PLOT_STATUS_LABEL: Record<PlotStatus, string> = {
  1: "Available",
  2: "Reserved",
  3: "Sold",
};

export type GravePlot = {
  plotId: number;
  orgId: number;
  section: string;
  plotRow: string;
  plotNumber: string;
  plotType: PlotType;
  status: PlotStatus;
  latitude: number | null;
  longitude: number | null;
  serviceOrderId: number | null;
  holderName: string;
};

export type PublicGravePlot = {
  plotId: number;
  orgId: number;
  cemeteryName: string;
  section: string;
  plotRow: string;
  plotNumber: string;
  plotType: PlotType;
  latitude: number | null;
  longitude: number | null;
};

export type PlotBatch = {
  orgId: number;
  section: string;
  plotRow: string;
  fromNumber: number;
  toNumber: number;
  plotType: PlotType;
};

export const MAX_PLOTS_PER_BATCH = 500;

/** Same rule as GravePlotRules.CanMove: forward only, or release a reservation. */
export const canMove = (from: PlotStatus, to: PlotStatus): boolean =>
  (from === PLOT_STATUS.AVAILABLE && (to === PLOT_STATUS.RESERVED || to === PLOT_STATUS.SOLD)) ||
  (from === PLOT_STATUS.RESERVED && (to === PLOT_STATUS.SOLD || to === PLOT_STATUS.AVAILABLE));

export const validateBatch = (b: PlotBatch): string | null => {
  if (!b.orgId || b.orgId <= 0) return "Choose an active provider first.";
  if (!b.section.trim()) return "Give the section the plots are in.";
  if (!Number.isInteger(b.fromNumber) || !Number.isInteger(b.toNumber) || b.fromNumber <= 0 || b.toNumber < b.fromNumber)
    return "Plot numbers run from a first number up to a last one.";
  if (b.toNumber - b.fromNumber + 1 > MAX_PLOTS_PER_BATCH) return `Add at most ${MAX_PLOTS_PER_BATCH} plots at a time.`;
  return null;
};

/** "B · Row 4 · 12", or without the row when there is none. */
export const plotPlace = (p: { section: string; plotRow: string; plotNumber: string }): string =>
  [p.section, p.plotRow ? `Row ${p.plotRow}` : "", p.plotNumber].filter(Boolean).join(" · ");

type Envelope<T> = { data?: T | null };

const unwrap = <T>(result: VgApiResult<Envelope<T>>): VgApiResult<T> =>
  result.error ? { data: null, error: result.error } : { data: (result.data?.data as T) ?? null, error: null };

export const readGravePlots = async (accessToken: string, orgId: number): Promise<VgApiResult<GravePlot[]>> => {
  const result = unwrap(await vgApiRequest<Envelope<GravePlot[]>>(accessToken, "Read_Grave_Plots", { orgId }));
  return result.error ? result : { data: result.data ?? [], error: null };
};

export const addGravePlots = async (accessToken: string, batch: PlotBatch): Promise<VgApiResult<number>> => {
  const invalid = validateBatch(batch);
  if (invalid) return { data: null, error: { message: invalid } };
  return unwrap(
    await vgApiRequest<Envelope<number>>(accessToken, "Create_Grave_Plots", {
      ...batch,
      section: batch.section.trim(),
      plotRow: batch.plotRow.trim(),
    })
  );
};

export const changePlotStatus = async (
  accessToken: string,
  plot: GravePlot,
  status: PlotStatus,
  holderName = ""
): Promise<VgApiResult<GravePlot>> => {
  if (!canMove(plot.status, status))
    return {
      data: null,
      error: {
        message: `A ${PLOT_STATUS_LABEL[plot.status].toLowerCase()} plot cannot become ${PLOT_STATUS_LABEL[status].toLowerCase()}.`,
      },
    };
  return unwrap(
    await vgApiRequest<Envelope<GravePlot>>(accessToken, "Update_Grave_Plot_Status", {
      orgId: plot.orgId,
      plotId: plot.plotId,
      status,
      holderName: holderName.trim(),
    })
  );
};

export const deleteGravePlot = async (accessToken: string, plot: GravePlot): Promise<VgApiResult<boolean>> =>
  unwrap(await vgApiRequest<Envelope<boolean>>(accessToken, "Delete_Grave_Plot", { orgId: plot.orgId, plotId: plot.plotId }));

/** Available plots in listed cemeteries. No sign-in needed. */
export const readAvailableGravePlots = async (filter: {
  orgId?: number;
  plotType?: PlotType;
}): Promise<VgApiResult<PublicGravePlot[]>> => {
  const result = unwrap(await vgPublicRequest<Envelope<PublicGravePlot[]>>("Read_Available_Grave_Plots", filter));
  return result.error ? result : { data: result.data ?? [], error: null };
};
